import { PageLoader } from '@/components/PageLoader';
import { useState } from 'react';
import dayjs from 'dayjs';

import { useLetterhead } from '@/api/bookings';
import {
  usePayables,
  useReceivables,
  type CustomerReceivable,
  type VendorPayable,
} from '@/api/outstanding';
import { hueClasses } from '@/components/hues';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { whatsappLink } from '@core/customers/words';
import { formatDocumentDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';
import type { ReceivableKind } from '@core/generated/enums';
import {
  ageTone,
  ageingBands,
  dueWords,
  owedForWords,
  owedOnWords,
} from '@core/outstanding/ageing';
import {
  customerStatement,
  overSixty,
  reminderText,
  vendorOwesWords,
  vendorStatement,
  type Statement,
} from '@core/outstanding/statement';
import { canAct } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { SheetPrint } from '../print/SheetPrint';
import { statementSheet } from '@core/print/sheetHtml';

type Chosen = { kind: 'customer'; id: number } | { kind: 'vendor'; id: number };

const rupees = (value: number) => `₹${formatAmount(value)}`;

/**
 * Commerce › Outstanding (Option A of the design canvas, 2026-10-22) — both sides on one page: the
 * four figures, who owes the shop (oldest first) beside whom the shop owes (due first), and the
 * chosen one's statement with its one next step — take the payment on a document, remind on
 * WhatsApp, pay a vendor — and Print statement. Every balance is the server's.
 */
export function OutstandingPage({
  session,
  onOpen,
}: {
  session: Session;
  onOpen: (key: string, view?: string, find?: string, record?: number) => void;
}) {
  const branchId = session.branch?.id;
  const receivables = useReceivables(branchId);
  const payables = usePayables(branchId);
  const letterhead = useLetterhead(branchId);
  const [chosen, setChosen] = useState<Chosen | null>(null);
  const [printing, setPrinting] = useState<Statement | null>(null);
  const today = dayjs().format('YYYY-MM-DD');

  const customers = receivables.data?.customers ?? [];
  const vendors = payables.data?.vendors ?? [];
  const current: Chosen | null =
    chosen ??
    (customers[0]
      ? { kind: 'customer', id: customers[0].customerId }
      : vendors[0]
        ? { kind: 'vendor', id: vendors[0].vendorId }
        : null);
  const customer =
    current?.kind === 'customer' ? customers.find((c) => c.customerId === current.id) : undefined;
  const vendor =
    current?.kind === 'vendor' ? vendors.find((v) => v.vendorId === current.id) : undefined;
  const late = receivables.data ? overSixty(receivables.data) : null;
  const msmeSoon = vendors.find((v) => v.isMsme && v.items.some((item) => item.daysPastDue > -7));

  if (printing && letterhead.data) {
    const shop = letterhead.data;

    return (
      <SheetPrint
        eyebrow="Outstanding"
        title={`Statement — ${printing.party.name}`}
        remember="statement"
        build={(paper, rollLength) =>
          statementSheet({ ...printing, letterhead: shop }, paper, rollLength)
        }
        onClose={() => setPrinting(null)}
      />
    );
  }

  return (
    <>
      <PageHeading
        title="Outstanding"
        subtitle="Who owes the shop, and whom the shop owes — customers, suppliers, launderers and tailors."
      />

      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Figure
          label="To receive"
          value={receivables.data ? rupees(receivables.data.total) : '—'}
          hint={`${customers.length} customer${customers.length === 1 ? '' : 's'}`}
          icon="icon icon-arrow-down-left"
          tile={hueClasses.primary.tile}
        />
        <Figure
          label="Over 60 days"
          value={late ? rupees(late.amount) : '—'}
          hint={late ? `${late.customers} customer${late.customers === 1 ? '' : 's'}` : ''}
          icon="icon icon-clock"
          tile={hueClasses.purple.tile}
        />
        <Figure
          label="To pay"
          value={payables.data ? rupees(payables.data.total) : '—'}
          hint={`${vendors.length} vendor${vendors.length === 1 ? '' : 's'}`}
          icon="icon icon-arrow-up-right"
          tile={hueClasses.teal.tile}
        />
        <Figure
          label="Due this week"
          value={payables.data ? rupees(payables.data.dueThisWeek) : '—'}
          hint={
            msmeSoon
              ? `MSME: ${msmeSoon.name}, ${dueWords(msmeSoon.items[0]?.daysPastDue ?? 0)}`
              : 'from vendors'
          }
          icon="icon icon-calendar-clock"
          tile={hueClasses.info.tile}
        />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-6 xl:col-span-4">
          <Card>
            <ListHead title="Customers owe us" note="oldest first" />
            {receivables.isLoading ? (
              <PageLoader compact label="Loading what customers owe" />
            ) : customers.length === 0 ? (
              <EmptyState
                icon="icon icon-circle-check"
                title="No customer owes anything"
                hint="A booking shows here once something has gone out and is not paid; a sale, once it is left part paid."
              />
            ) : (
              <ul className="m-0 flex list-none flex-col ps-0">
                {customers.map((row) => (
                  <li key={row.customerId} className="list-none">
                    <PickRow
                      active={current?.kind === 'customer' && current.id === row.customerId}
                      onClick={() => setChosen({ kind: 'customer', id: row.customerId })}
                      name={row.name}
                      line={owedOnWords(row.items)}
                      badge={
                        <StatusBadge tone={ageTone(row.oldestDays, 'receive')}>
                          {owedForWords(row.oldestDays)}
                        </StatusBadge>
                      }
                      amount={rupees(row.total)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="col-span-12 lg:col-span-6 xl:col-span-4">
          <Card>
            <ListHead title="We owe vendors" note="due first" />
            {payables.isLoading ? (
              <PageLoader compact label="Loading what we owe" />
            ) : vendors.length === 0 ? (
              <EmptyState
                icon="icon icon-circle-check"
                title="Nothing is owed to any vendor"
                hint="Supplier bills and finished outside cleaning or alteration jobs show here until they are paid."
              />
            ) : (
              <ul className="m-0 flex list-none flex-col ps-0">
                {vendors.map((row) => (
                  <li key={row.vendorId} className="list-none">
                    <PickRow
                      active={current?.kind === 'vendor' && current.id === row.vendorId}
                      onClick={() => setChosen({ kind: 'vendor', id: row.vendorId })}
                      name={row.name}
                      line={vendorOwesWords(row)}
                      badge={
                        row.isMsme ? (
                          <StatusBadge tone={row.msmeOverdue ? 'negative' : 'warning'}>
                            MSME
                          </StatusBadge>
                        ) : null
                      }
                      amount={
                        row.balance < 0 ? `${rupees(-row.balance)} ahead` : rupees(row.balance)
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="col-span-12 xl:col-span-4">
          {customer ? (
            <CustomerPanel
              session={session}
              customer={customer}
              shop={{
                name: letterhead.data?.tenantName ?? session.branch?.name ?? 'the shop',
                phone: letterhead.data?.phone,
              }}
              onTake={(kind, id) =>
                onOpen(kind === 'Sale' ? 'pos' : 'bookings', undefined, undefined, id)
              }
              onPrint={() => setPrinting(customerStatement(customer, today))}
            />
          ) : vendor ? (
            <VendorPanel
              session={session}
              vendor={vendor}
              onPay={() => onOpen('vendorpayments', undefined, undefined, vendor.vendorId)}
              onDesk={() => onOpen('purchases', undefined, undefined, vendor.vendorId)}
              onPrint={() => setPrinting(vendorStatement(vendor, today))}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------------------------------------ */

function Figure({
  label,
  value,
  hint,
  icon,
  tile,
}: {
  label: string;
  value: string;
  hint: string;
  icon: string;
  tile: string;
}) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-default mb-1 truncate text-[length:var(--scale-list-name)]">{label}</p>
          <h3 className="font-mono-nums mb-0 text-2xl font-bold text-gray-900 max-lg:text-xl">
            {value}
          </h3>
          <p className="text-default mt-0.5 mb-0 truncate text-[length:var(--scale-list-meta)]">
            {hint}
          </p>
        </div>
        <span
          className={`flex size-10 shrink-0 items-center justify-center rounded-md ${tile}`}
          aria-hidden="true"
        >
          <i className={`${icon} text-lg text-white`} />
        </span>
      </div>
    </Card>
  );
}

function ListHead({ title, note }: { title: string; note: string }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <p className="mb-0 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
        {title}
      </p>
      <span className="text-default text-[11px]">{note}</span>
    </div>
  );
}

function PickRow({
  active,
  onClick,
  name,
  line,
  badge,
  amount,
}: {
  active: boolean;
  onClick: () => void;
  name: string;
  line: string;
  badge: React.ReactNode;
  amount: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-start ${
        active ? 'border-primary bg-primary-transparent' : 'hover:bg-light border-transparent'
      }`}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold text-gray-900">{name}</span>
        <span className="text-default block truncate text-[11px]">{line}</span>
      </span>
      {badge}
      <span className="font-mono-nums text-end text-xs font-semibold text-gray-900">{amount}</span>
    </button>
  );
}

function Bands({ bands }: { bands: { label: string; amount: number }[] }) {
  return (
    <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
      {bands.map((band) => (
        <div key={band.label} className="bg-light rounded-md px-2 py-1.5 text-center">
          <p className="text-default mb-0 text-[11px]">{band.label}</p>
          <p className="font-mono-nums mb-0 text-xs font-semibold text-gray-900">
            {band.amount === 0 ? '—' : formatAmount(band.amount)}
          </p>
        </div>
      ))}
    </div>
  );
}

function CustomerPanel({
  session,
  customer,
  shop,
  onTake,
  onPrint,
}: {
  session: Session;
  customer: CustomerReceivable;
  shop: { name: string; phone: string | null | undefined };
  onTake: (kind: ReceivableKind, id: number) => void;
  onPrint: () => void;
}) {
  const mayTake = canAct(session, 'RentalOrder:Edit') || canAct(session, 'SalesInvoice:Create');

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-default mb-0 text-[11px]">Customer · {customer.mobile}</p>
          <p className="mb-0 text-lg font-bold text-gray-900">{customer.name}</p>
        </div>
        <div className="text-end">
          <p className="text-default mb-0 text-[11px]">Owes</p>
          <p className="font-mono-nums mb-0 text-lg font-bold text-gray-900">
            {rupees(customer.total)}
          </p>
        </div>
      </div>

      <div className="mt-3">
        <Bands bands={ageingBands(customer.ageing, 'receive')} />
      </div>

      <ul className="mt-3 flex list-none flex-col gap-1.5 ps-0">
        {customer.items.map((item) => (
          <li
            key={`${item.kind}:${item.id}`}
            className="border-border-color flex list-none items-center justify-between gap-2 rounded-md border px-3 py-2"
          >
            <div className="min-w-0">
              <p className="font-mono-nums mb-0 text-xs font-semibold text-gray-900">
                {item.reference}
              </p>
              <p className="text-default mb-0 text-[11px]">
                {item.kind === 'Sale' ? 'Sale' : 'Booking'} · since {formatDocumentDate(item.since)}{' '}
                · {owedForWords(item.days)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono-nums text-xs font-semibold text-gray-900">
                {rupees(item.outstanding)}
              </span>
              {mayTake && (
                <Button
                  variant={customer.items.length === 1 ? 'primary' : 'secondary'}
                  onClick={() => onTake(item.kind, item.id)}
                >
                  Take payment
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap gap-2">
        {customer.mobile && (
          <Button
            icon="icon icon-message-circle"
            onClick={() =>
              window.open(
                whatsappLink(customer.mobile, reminderText(customer, shop)),
                '_blank',
                'noopener',
              )
            }
          >
            Remind on WhatsApp
          </Button>
        )}
        <Button icon="icon icon-printer" onClick={onPrint}>
          Print statement
        </Button>
      </div>
      <p className="text-default mt-2 mb-0 text-[11px]">
        WhatsApp opens with the reminder typed in; you press send. Nothing is recorded.
      </p>
    </Card>
  );
}

function VendorPanel({
  session,
  vendor,
  onPay,
  onDesk,
  onPrint,
}: {
  session: Session;
  vendor: VendorPayable;
  onPay: () => void;
  onDesk: () => void;
  onPrint: () => void;
}) {
  const first = vendor.items[0];

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-default mb-0 text-[11px]">
            {[vendor.isMsme ? 'MSME' : null, vendor.mobile].filter(Boolean).join(' · ') || 'Vendor'}
          </p>
          <p className="mb-0 text-lg font-bold text-gray-900">{vendor.name}</p>
        </div>
        <div className="text-end">
          <p className="text-default mb-0 text-[11px]">We owe</p>
          <p className="font-mono-nums mb-0 text-lg font-bold text-gray-900">
            {rupees(Math.max(0, vendor.balance))}
          </p>
        </div>
      </div>

      {vendor.isMsme && first && (
        <div className="mt-3">
          <Notice tone={vendor.msmeOverdue ? 'danger' : 'warning'} title="MSME supplier">
            Pay by {formatDocumentDate(first.dueDate)} ({dueWords(first.daysPastDue)}). After that
            the MSMED Act adds interest at three times the bank rate.
          </Notice>
        </div>
      )}

      <div className="mt-3">
        <Bands bands={ageingBands(vendor.ageing, 'pay')} />
      </div>

      <ul className="mt-3 flex list-none flex-col gap-1.5 ps-0">
        {vendor.items.map((item) => (
          <li
            key={`${item.kind}:${item.id}`}
            className="border-border-color flex list-none items-center justify-between gap-2 rounded-md border px-3 py-2"
          >
            <div className="min-w-0">
              <p className="mb-0 text-xs font-semibold text-gray-900">
                {item.description}{' '}
                <span className="text-default font-mono-nums font-normal">{item.reference}</span>
              </p>
              <p className="text-default mb-0 text-[11px]">
                {item.kind === 'Bill' ? 'Due' : 'Back'} {formatDocumentDate(item.dueDate)} ·{' '}
                {dueWords(item.daysPastDue)}
              </p>
            </div>
            <span className="font-mono-nums text-xs font-semibold text-gray-900">
              {rupees(item.outstanding)}
            </span>
          </li>
        ))}
      </ul>
      {vendor.onAccount > 0 && (
        <p className="text-default mt-2 mb-0 text-[12px]">
          {rupees(vendor.onAccount)} of ours is already on account with them.
        </p>
      )}
      {vendor.uncostedJobs > 0 && (
        <p className="mt-2 mb-0 text-[12px] text-[color:var(--color-warning)]">
          {vendor.uncostedJobs} finished job{vendor.uncostedJobs === 1 ? ' has' : 's have'} no cost
          yet — add it on the job.
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {vendor.balance > 0 && canAct(session, 'VendorPayment:Create') && (
          <Button variant="primary" icon="icon icon-wallet" onClick={onPay}>
            Pay {rupees(vendor.balance)}
          </Button>
        )}
        <Button icon="icon icon-truck" onClick={onDesk}>
          Open on the supplier desk
        </Button>
        <Button icon="icon icon-printer" onClick={onPrint}>
          Print statement
        </Button>
      </div>
    </Card>
  );
}
